import { motion } from "@/lib/motion";
import { OptimizedGlassCard } from "@/components/ui/OptimizedGlassCard";
import { ButtonCustom } from "@/components/ui/button-custom";
import { Link } from "react-router-dom";
import { ArrowLeft, Calendar, Clock, Tag } from "lucide-react";
import { useState, useMemo, useEffect } from "react";
import { Seo } from "@/components/seo/Seo";
import blogPostsData from "@/content/blog-posts.json";
import { getAllPosts } from "@/lib/blogStorage";

/**
 * Blog post structure for type safety
 */
interface BlogPost {
  id: string;
  title: string;
  excerpt: string;
  category: string;
  date: string;
  readTime: string;
  image: string;
  imageAlt: string;
  tags: string[];
}

/**
 * Raw blog post structure from JSON
 */
interface RawBlogPost {
  id: string;
  title: string;
  metaDescription: string;
  category: string;
  date: string;
  readTime: string;
  image: string;
  imageAlt: string;
  tags: string[];
}

// Initial transform from static JSON (used for SSR + first paint)
const initialBlogPosts: BlogPost[] = Object.values(
  blogPostsData as Record<string, RawBlogPost>
).map((post) => ({
  id: post.id,
  title: post.title,
  excerpt: post.metaDescription,
  category: post.category,
  date: post.date,
  readTime: post.readTime,
  image: post.image,
  imageAlt: post.imageAlt,
  tags: post.tags,
}));

const categories = [
  "الكل",
  "التجارة الإلكترونية",
  "التسويق الرقمي",
  "SEO",
  "الإعلانات المدفوعة",
  "تطوير المواقع",
];

// BreadcrumbList Schema for Blog page
const breadcrumbSchema = {
  "@context": "https://schema.org",
  "@type": "BreadcrumbList",
  itemListElement: [
    {
      "@type": "ListItem",
      position: 1,
      name: "الرئيسية",
      item: "https://itqan.agency",
    },
    {
      "@type": "ListItem",
      position: 2,
      name: "المدونة",
      item: "https://itqan.agency/blog",
    },
  ],
};

// Blog Schema with articles
const getBlogSchema = (posts: BlogPost[]) => ({
  "@context": "https://schema.org",
  "@type": "Blog",
  name: "مدونة إتقان",
  description:
    "مقالات وأدلة شاملة عن التسويق الرقمي والتجارة الإلكترونية في السعودية",
  url: "https://itqan.agency/blog",
  publisher: {
    "@type": "Organization",
    name: "إتقان",
    logo: {
      "@type": "ImageObject",
      url: "https://itqan.agency/brand/itqan-logo-main.jpg",
    },
  },
  blogPost: posts.slice(0, 5).map((post) => ({
    "@type": "BlogPosting",
    headline: post.title,
    description: post.excerpt,
    url: `https://itqan.agency/blog/${post.id}`,
    datePublished: post.date,
    image: post.image.startsWith("http")
      ? post.image
      : `https://itqan.agency${post.image}`,
    author: {
      "@type": "Person",
      name: "فريق إتقان التسويقي",
    },
  })),
});

// CollectionPage Schema for better indexing
const collectionPageSchema = {
  "@context": "https://schema.org",
  "@type": "CollectionPage",
  name: "مدونة إتقان | مقالات التسويق الرقمي",
  description:
    "اكتشف أحدث مقالات ونصائح التسويق الرقمي، SEO، التجارة الإلكترونية، وتطوير المواقع من خبراء إتقان في السعودية.",
  url: "https://itqan.agency/blog",
  isPartOf: {
    "@type": "WebSite",
    name: "منصة إتقان",
    url: "https://itqan.agency",
  },
  about: {
    "@type": "Thing",
    name: "التسويق الرقمي والتجارة الإلكترونية",
  },
  inLanguage: "ar-SA",
};

const Blog = () => {
  const [selectedCategory, setSelectedCategory] = useState("الكل");
  const [blogPosts, setBlogPosts] = useState<BlogPost[]>(initialBlogPosts);

  // Merge in any locally-published drafts after mount.
  useEffect(() => {
    const merged = Object.values(getAllPosts()).map((post) => ({
      id: post.id,
      title: post.title,
      excerpt: post.metaDescription,
      category: post.category,
      date: post.date,
      readTime: post.readTime,
      image: post.image,
      imageAlt: post.imageAlt,
      tags: post.tags,
    }));
    setBlogPosts(merged);
  }, []);

  const filteredPosts =
    selectedCategory === "الكل"
      ? blogPosts
      : blogPosts.filter((post) => post.category === selectedCategory);

  const categoryCounts = useMemo(() => {
    return categories.reduce(
      (acc, cat) => {
        if (cat === "الكل") {
          acc[cat] = blogPosts.length;
        } else {
          acc[cat] = blogPosts.filter((post) => post.category === cat).length;
        }
        return acc;
      },
      {} as Record<string, number>
    );
  }, [blogPosts]);

  const jsonLdSchemas = [
    breadcrumbSchema,
    getBlogSchema(blogPosts),
    collectionPageSchema,
  ];

  return (
    <>
      <Seo
        title="مدونة إتقان | مقالات التسويق الرقمي والتجارة الإلكترونية في السعودية"
        description="اكتشف أحدث مقالات ونصائح التسويق الرقمي، SEO، التجارة الإلكترونية، وتطوير المواقع من خبراء إتقان في السعودية. دليلك الشامل للنجاح الرقمي."
        canonical="/blog"
        ogImage="https://itqan.agency/og-image.jpg"
        ogType="website"
        jsonLd={jsonLdSchemas}
      />

      <div className="min-h-screen pt-24">
        {/* Hero Section */}
        <section className="relative py-24 overflow-hidden bg-surface">
          <div className="absolute inset-0 opacity-5">
            <div className="absolute inset-0 bg-gradient-to-br from-sand via-transparent to-obs" />
          </div>

          <div className="container mx-auto px-4 relative z-10">
            <motion.div
              initial={{ opacity: 0, y: 30 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.8 }}
              className="text-center max-w-4xl mx-auto"
            >
              <h1 className="text-5xl md:text-7xl font-display font-bold mb-6 foil">
                <strong>مدونة إتقان</strong> للتسويق الرقمي
              </h1>
              <p className="text-xl md:text-2xl text-foreground/80 mb-8">
                <strong>أحدث المقالات</strong> والنصائح في{" "}
                <strong>التسويق الرقمي</strong>،{" "}
                <strong>التجارة الإلكترونية</strong>، و
                <strong>تطوير المواقع</strong>
              </p>
            </motion.div>
          </div>
        </section>

        {/* Filter Bar */}
        <section className="sticky top-20 z-30 glass border-b border-foreground/10 py-4 bg-surface/80 backdrop-blur-lg">
          <div className="container mx-auto px-4">
            <nav aria-label="تصفية المقالات حسب التصنيف">
              <div className="flex items-center gap-3 overflow-x-auto pb-2 scrollbar-hide">
                <Tag
                  className="w-4 h-4 text-sand flex-shrink-0"
                  aria-hidden="true"
                />
                {categories.map((cat) => (
                  <button
                    key={cat}
                    onClick={() => setSelectedCategory(cat)}
                    aria-pressed={selectedCategory === cat}
                    className={`px-4 py-2 rounded-lg text-sm font-medium whitespace-nowrap transition-all ${
                      selectedCategory === cat
                        ? "bg-sand text-obs"
                        : "glass hover:bg-foreground/5"
                    }`}
                  >
                    {cat}
                    {categoryCounts[cat] > 0 && (
                      <span
                        className={`mr-1.5 ${selectedCategory === cat ? "text-obs/70" : "text-foreground/50"}`}
                      >
                        ({categoryCounts[cat]})
                      </span>
                    )}
                  </button>
                ))}
              </div>
            </nav>
          </div>
        </section>

        {/* Blog Posts Grid */}
        <section className="py-16 bg-surface-2" aria-label="قائمة المقالات">
          <div className="container mx-auto px-4">
            <h2 className="text-3xl md:text-4xl font-display font-bold mb-8 text-center">
              <strong>أحدث المقالات والأدلة الشاملة</strong>
            </h2>
            <p className="text-xl md:text-2xl text-center text-foreground/70 mb-12">
              <strong>
                تصفح مقالاتنا المتخصصة في التسويق الرقمي والتجارة الإلكترونية
              </strong>
            </p>
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
              {filteredPosts.map((post, index) => (
                <motion.article
                  key={post.id}
                  initial={{ opacity: 0, y: 20 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  viewport={{ once: true }}
                  transition={{ duration: 0.5, delay: index * 0.1 }}
                >
                  <Link
                    to={`/blog/${post.id}`}
                    aria-label={`اقرأ مقال: ${post.title}`}
                  >
                    <OptimizedGlassCard className="h-full group hover:scale-[1.02] transition-transform duration-300">
                      {/* Image */}
                      <div className="relative h-56 -m-6 md:-m-8 mb-6 md:mb-8 overflow-hidden rounded-t-lg">
                        <img
                          src={post.image}
                          alt={post.imageAlt}
                          loading="lazy"
                          className="w-full h-full object-cover transition-transform duration-700 group-hover:scale-110"
                        />
                        <div className="absolute inset-0 bg-gradient-to-t from-obs via-obs/50 to-transparent" />
                        <div className="absolute top-4 right-4">
                          <span className="glass px-3 py-1 text-xs text-sand rounded-full">
                            {post.category}
                          </span>
                        </div>
                      </div>

                      {/* Content */}
                      <div className="space-y-4">
                        <h3 className="text-xl font-display font-bold group-hover:text-sand transition-colors">
                          <strong>{post.title}</strong>
                        </h3>

                        <p className="text-foreground/70 text-sm leading-relaxed line-clamp-3">
                          {post.excerpt}
                        </p>

                        {/* Meta Info */}
                        <div className="flex items-center gap-4 text-xs text-foreground/60">
                          <div className="flex items-center gap-1">
                            <Calendar
                              className="w-3.5 h-3.5"
                              aria-hidden="true"
                            />
                            <time dateTime={post.date}>
                              {new Date(post.date).toLocaleDateString("ar-SA")}
                            </time>
                          </div>
                          <div className="flex items-center gap-1">
                            <Clock className="w-3.5 h-3.5" aria-hidden="true" />
                            <span>{post.readTime}</span>
                          </div>
                        </div>

                        {/* Tags */}
                        <div className="flex flex-wrap gap-2">
                          {post.tags.map((tag) => (
                            <span
                              key={tag}
                              className="text-xs px-2 py-1 rounded border border-sand/20 text-sand/70"
                            >
                              {tag}
                            </span>
                          ))}
                        </div>

                        {/* Read More Button */}
                        <ButtonCustom
                          variant="ghost"
                          size="sm"
                          className="w-full mt-4 group-hover:bg-sand/10"
                        >
                          اقرأ المزيد
                          <ArrowLeft className="w-4 h-4" aria-hidden="true" />
                        </ButtonCustom>
                      </div>
                    </OptimizedGlassCard>
                  </Link>
                </motion.article>
              ))}
            </div>

            {filteredPosts.length === 0 && (
              <div className="text-center py-16" role="status">
                <p className="text-xl text-foreground/60">
                  لا توجد مقالات في هذا التصنيف
                </p>
              </div>
            )}
          </div>
        </section>

        {/* CTA Section */}
        <section className="py-24 bg-surface" aria-label="دعوة للتواصل">
          <div className="container mx-auto px-4">
            <motion.div
              initial={{ opacity: 0, scale: 0.95 }}
              whileInView={{ opacity: 1, scale: 1 }}
              viewport={{ once: true }}
              transition={{ duration: 0.6 }}
              className="text-center max-w-3xl mx-auto"
            >
              <h2 className="text-4xl md:text-5xl font-display font-bold mb-6 foil">
                <strong>هل تريد تحسين نتائجك الرقمية؟</strong>
              </h2>
              <p className="text-xl text-foreground/70 mb-10">
                احجز استشارة مجانية مع خبرائنا اليوم
              </p>
              <ButtonCustom variant="foil" size="lg" asChild>
                <Link to="/contact">
                  احجز استشارتك المجانية
                  <ArrowLeft className="w-5 h-5" aria-hidden="true" />
                </Link>
              </ButtonCustom>
            </motion.div>
          </div>
        </section>
      </div>
    </>
  );
};

export default Blog;